import SideNav from './sections/SideNav';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '~/redux/store';
import { useRefreshCurrentUser } from '~/api';
import { TopNav } from './sections/TopNav';
import { useEffect, useState } from 'react';
import { canAccessNavPath, findNavItemByPath } from './config';
import { ThemedLayout } from './ThemedLayout';
import Intercom from '@intercom/messenger-js-sdk';
import { useGetAllUnits } from '~/api/unit';
import { useGetPorts } from '~/api/logistics';
import { setPorts, setUnits } from '~/redux/reducers/auth';
import type { Port } from '~/graphql/graphql';

export function AuthLayout() {
  const { isLoading, isError } = useRefreshCurrentUser();
  const { data: unitsdata } = useGetAllUnits();
  const { data: portsData } = useGetPorts();
  const role = useSelector((state: RootState) => state.auth.user?.role);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);

  const dispatch = useDispatch();

  useEffect(() => {
    if (unitsdata) {
      dispatch(setUnits(unitsdata.units || []));
    }
  }, [unitsdata, dispatch]);

  useEffect(() => {
    if (portsData) {
      dispatch(setPorts((portsData.ports || []) as Port[]));
    }
  }, [portsData, dispatch]);

  useEffect(() => {
    if (isLoading) return;
    if (!role) return;

    if (user?.id) {
      Intercom({
        app_id: 'jbh67q8f',
        user_id: user.id,
        name: user.externalId,
        email: user.externalId,
      });
    }

    if (
      !canAccessNavPath(role, location.pathname) &&
      findNavItemByPath(location.pathname)
    ) {
      navigate('/');
    }
  }, [isLoading, role, location.pathname, navigate, user]);

  if (isLoading) return <div>Loading your account…</div>;
  if (isError) return <div>Could not load your profile.</div>;

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <ThemedLayout>
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-neut-prim/50 z-40  lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
      <div
        className={`w-64 bg-nav-bg border-r border-neut-bg shadow-sm
                      h-screen fixed lg:static z-50 transition-transform duration-300
                      lg:translate-x-0 ${
                        isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
                      }`}
      >
        <SideNav onNavigate={() => setIsSidebarOpen(false)} />
      </div>
      <div className="w-full lg:w-[calc(100%-256px)] lg:ml-0 min-h-[100vh] max-h-[100vh] overflow-y-auto bg-neut-bg-alt">
        <TopNav onToggleSidebar={toggleSidebar} />
        <div className="w-full p-3 sm:p-6 lg:p-8">
          <Outlet />
        </div>
      </div>
    </ThemedLayout>
  );
}
